import { ProgramListItem } from "../admin/types";
import { listPublishedPrograms } from "./programs";

// Shared cache for CMS-published programs, used everywhere the public site
// needs to know about admin-created programs without hardcoding them
// (homepage grid, header nav, assessment matching — see PATTERNS.md). A
// module-level cache survives route changes within the same page load;
// localStorage survives a full reload so the very first paint after a
// refresh can use yesterday's list instead of blocking on a network round
// trip, then quietly revalidates in the background.
const STORAGE_KEY = "nvfo_cms_programs_cache_v1";
const TTL_MS = 5 * 60 * 1000;

type CacheEntry = { data: ProgramListItem[]; fetchedAt: number };

let memoryCache: CacheEntry | null = null;
let inFlight: Promise<ProgramListItem[]> | null = null;

// Components that already resolved a stale getProgramsAsync() call (most
// notably Header, which mounts once for the whole tab and never re-fetches)
// would otherwise never learn that a background revalidation came back with
// new data -- e.g. a program added in the admin panel wouldn't reach the nav
// until a hard refresh. Every background fetch below notifies these too.
const listeners = new Set<(data: ProgramListItem[]) => void>();

// Subscribes to every future cache update (background revalidations
// included), returning an unsubscribe function. Call once in a component's
// mount effect alongside its own getProgramsAsync() call, so a revalidation
// that resolves after the initial render still lands in its state.
export function subscribeToProgramsCache(listener: (data: ProgramListItem[]) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// The above only reaches listeners within this same tab -- an admin edit
// made in a different tab (or window) only ever touches that tab's own
// in-memory cache and localStorage never fires a same-tab "storage" event,
// so this tab's mounted components would otherwise stay stale until their
// own next reload. The native `storage` event fires in every OTHER tab on
// the same origin whenever localStorage changes, so listening for it here
// gets cross-tab propagation for free, no extra server/socket infrastructure.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const entry = JSON.parse(e.newValue) as CacheEntry;
      memoryCache = entry;
      listeners.forEach((listener) => listener(entry.data));
    } catch {
      // corrupt payload written by another tab -- ignore, keep our own cache
    }
  });
}

function readStorage(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CacheEntry) : null;
  } catch {
    return null; // private browsing, quota, or corrupt JSON -- just skip the cache
  }
}

function writeStorage(entry: CacheEntry) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // ignore -- in-memory cache still works for the rest of this page load
  }
}

function fetchAndCache(): Promise<ProgramListItem[]> {
  if (!inFlight) {
    inFlight = listPublishedPrograms()
      .then((data) => {
        const entry = { data, fetchedAt: Date.now() };
        memoryCache = entry;
        writeStorage(entry);
        listeners.forEach((listener) => listener(data));
        return data;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

// Non-blocking: whatever's cached right now (memory, else localStorage), or
// null if nothing's loaded yet. Never triggers a network request -- safe to
// call during a synchronous render.
export function getCachedProgramsSync(): ProgramListItem[] | null {
  if (!memoryCache) memoryCache = readStorage();
  return memoryCache?.data ?? null;
}

// Returns cached data immediately if present (even if stale), kicking off a
// background revalidation when past TTL; awaits a real fetch only when
// there's nothing cached at all. Callers that can render synchronously
// should prefer getCachedProgramsSync() + primeProgramsCache() instead of
// awaiting this, so a cold cache never blocks first paint.
export async function getProgramsAsync(): Promise<ProgramListItem[]> {
  if (!memoryCache) memoryCache = readStorage();
  const isFresh = memoryCache && Date.now() - memoryCache.fetchedAt < TTL_MS;
  if (memoryCache && !isFresh) fetchAndCache().catch(() => {});
  if (memoryCache) return memoryCache.data;
  return fetchAndCache();
}

// Fire-and-forget warmup -- call once near app start so the cache is likely
// populated by the time a component actually needs it.
export function primeProgramsCache(): void {
  getProgramsAsync().catch(() => {});
}

// Call after an admin create/update/delete/publish so an already-open public
// tab (the common case: an admin testing a change right after saving it, in
// the same browser session) reflects it immediately -- otherwise the TTL
// above means the change wouldn't reach any public page for up to 5 minutes,
// and a persistently-mounted component like Header not at all until a hard
// refresh. Clears the stale entry outright rather than just re-fetching, so
// even a concurrent in-flight fetch that started before this edit can't win
// the race and repopulate the cache with data that's already out of date.
export function invalidateProgramsCache(): void {
  memoryCache = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore -- nothing to clear
  }
  fetchAndCache().catch(() => {});
}
